// ── Shared Types ──────────────────────────────────────────────────────────────
export interface ChartDataItem {
  name: string;
  value: number;
  team?: string;
}

export interface VisualizationData {
  query_type?: string;
  chart_type?: string;
  data?: any[];
  labels?: string[];
  supported?: boolean;         // false  → backend blocked this query type
  message?: string;            // human-readable reason for the popup
  error?: string;              // legacy error string (existing branch)
  values?: number[];
  sql_query?: string;
  limit?: number;
  total_records?: number;
}

export interface GraphProps {
  chartData: ChartDataItem[];
  chartType: "bar" | "pie";
  activeIndex: number | null;
  setActiveIndex: (i: number | null) => void;
  chartReady: boolean;
  teamColor: string;
  total: number;
}

// ── Colors ────────────────────────────────────────────────────────────────────
export const BAR_COLOR = "#00a6ff";

export const PIE_COLORS = [
  "#6C63FF", "#FF6B9D", "#00D4AA", "#FFB347",
  "#4ECDC4", "#FF6B6B", "#45B7D1", "#96CEB4", "#FFEAA7", "#DDA0DD",
];

// ── Compute dense rank for an array of numeric values (descending) ─────────────
function computeDenseRanks(values: number[]): number[] {
  const sorted = [...new Set(values)].sort((a, b) => b - a);
  const rankMap = new Map<number, number>();
  sorted.forEach((v, i) => rankMap.set(v, i + 1));
  return values.map((v) => rankMap.get(v) ?? 1);
}

// ── Build chartData from raw visualizationData ────────────────────────────────
export function buildChartData(
  visualizationData: VisualizationData,
  rankLimit: number
): ChartDataItem[] {
  let allItems: { name: string; value: number; team?: string }[] = [];

  if (
    Array.isArray(visualizationData.labels) &&
    visualizationData.labels.length > 0 &&
    Array.isArray(visualizationData.values) &&
    visualizationData.values.length > 0
  ) {
    allItems = visualizationData.labels.map((label, i) => ({
      name: String(label),
      value: Number(visualizationData.values![i] ?? 0),
    }));
  } else if (Array.isArray(visualizationData.data) && visualizationData.data.length > 0) {
    allItems = visualizationData.data.map((item: any) => {
      if (Array.isArray(item)) {
        return {
          name: String(item[0] ?? ""),
          team: item.length >= 3 ? String(item[1] ?? "") : undefined,
          value: Number(item[item.length - 1] ?? 0),
        };
      }
      return {
        name: item.label ?? item.name ?? item.player ?? String(item),
        value: Number(item.value ?? item.count ?? item.total ?? 0),
      };
    });
  }

  if (allItems.length === 0) return [];

  // Compute dense ranks for all items
  const values = allItems.map((item) => item.value);
  const denseRanks = computeDenseRanks(values);

  // Filter to only include items with rank <= rankLimit
  const filteredItems: ChartDataItem[] = [];
  for (let i = 0; i < allItems.length; i++) {
    if (denseRanks[i] <= rankLimit) {
      filteredItems.push(allItems[i]);
    }
  }

  return filteredItems;
}

// ── Chart title per query type ────────────────────────────────────────────────
export function buildChartTitle(queryType: string | undefined, title: string): string {
  const qt = queryType?.toLowerCase();
  const qtNorm = (qt ?? "").trim();
  
  // Helper function to convert to title case
  const toTitleCase = (str: string) => {
    return str
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };
  
  // Support backend variants like "min_season" / "max_season" by folding them
  // into the existing chart title buckets.
  const qtBucket =
    qtNorm === "min" || qtNorm.startsWith("min_") ? "min" :
    qtNorm === "max" || qtNorm.startsWith("max_") ? "max" :
    qtNorm === "streak" || qtNorm.startsWith("streak_") ? "streak" :
    qtNorm === "ltw" || qtNorm.startsWith("ltw_") ? "ltw" :
    qtNorm;

  switch (qtBucket) {
    case "basic":
    case "simple":  return title ? `${toTitleCase(title)} (No of Occurrences)` : "Frequency of Players";
    case "ltw":     return title ? toTitleCase(title) : "Last Time When";
    case "min":     return title ? toTitleCase(title) : "Players With Minimum Values";
    case "max":     return title ? toTitleCase(title) : "Players With Maximum Values";
    case "streak":  return title ? toTitleCase(title) : "Player Streaks";
    default:        return title ? toTitleCase(title) : "Data Visualization";
  }
}

// ── Graph type dropdown options ───────────────────────────────────────────────
// Both bar and pie are always available — same as original GraphPopupModal
export function getGraphTypeOptions() {
  return [
    { value: "bar", label: "Bar Chart" },
    { value: "pie", label: "Pie Chart" },
  ];
}

// ── Shared Donut/Pie ApexOptions factory ──────────────────────────────────────
export function buildDonutOptions(
  chartData: ChartDataItem[],
  total: number,
  activeIndex: number | null,
  setActiveIndex: (i: number | null) => void
) {
  return {
    chart: {
      type: "donut" as const,
      toolbar: { show: false },
      animations: {
        enabled: true,
        speed: 800,
        animateGradually: { enabled: true, delay: 120 },
        dynamicAnimation: { enabled: true, speed: 400 },
      },
      events: {
        dataPointMouseEnter: (_e: any, _chart: any, opts: any) =>
          setActiveIndex(opts.dataPointIndex),
        dataPointMouseLeave: () => setActiveIndex(null),
      },
    },
    labels: chartData.map((d) => d.name),
    colors: PIE_COLORS,
    dataLabels: { enabled: false },
    legend: { show: false },
    stroke: { width: 3, colors: ["#ffffff"] },
    plotOptions: {
      pie: {
        donut: {
          size: "45%",
          labels: {
            show: true,
            name: {
              show: true, fontSize: "11px", fontWeight: 700, color: "#888", offsetY: -6,
              formatter: () =>
                activeIndex !== null ? chartData[activeIndex]?.name ?? "Total" : "Total",
            },
            value: {
              show: true, fontSize: "24px", fontWeight: 800,
              color: activeIndex !== null
                ? PIE_COLORS[activeIndex % PIE_COLORS.length]
                : "#444",
              offsetY: 6,
              formatter: (val: string) => Number(val).toLocaleString(),
            },
            total: {
              show: true, showAlways: true,
              label: activeIndex !== null
                ? chartData[activeIndex]?.name ?? "Total"
                : "Total",
              fontSize: "11px", fontWeight: 600, color: "#aaa",
              formatter: () =>
                activeIndex !== null
                  ? chartData[activeIndex]?.value?.toLocaleString() ?? ""
                  : total.toLocaleString(),
            },
          },
        },
        expandOnClick: true,
        startAngle: -90,
        endAngle: 270,
      },
    },
    dropShadow: { enabled: true, top: 4, left: 4, blur: 12, color: "#6C63FF", opacity: 0.15 },
    states: {
      hover: { filter: { type: "lighten" as const, value: 0.15 } },
      active: { filter: { type: "lighten" as const, value: 0.1 } },
    },
    tooltip: {
      custom: ({ series, seriesIndex, w }: any) => {
        const name = w.globals.labels[seriesIndex];
        const val = series[seriesIndex];
        const pct = total > 0 ? ((val / total) * 100).toFixed(1) : "0";
        const color = PIE_COLORS[seriesIndex % PIE_COLORS.length];
        return `
          <div style="
            background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
            border: 2px solid ${color};
            border-radius: 12px;
            padding: 12px 18px;
            font-size: 13px;
            box-shadow: 0 8px 32px rgba(0,0,0,0.25), 0 0 0 1px ${color}33;
            min-width: 150px;">
            <p style="margin:0;font-weight:700;color:#f0f0f0;margin-bottom:6px;font-size:12px;letter-spacing:0.3px">${name}</p>
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px">
              <div style="width:10px;height:10px;border-radius:50%;background:${color};box-shadow:0 0 8px ${color};"></div>
              <p style="margin:0;color:${color};font-weight:800;font-size:16px">${Number(val).toLocaleString()}</p>
            </div>
            <div style="display:flex;align-items:center;gap:6px;background:${color}22;border-radius:6px;padding:3px 7px;">
              <span style="color:${color};font-size:11px;font-weight:700">${pct}%</span>
              <span style="color:#888;font-size:10px">of total</span>
            </div>
          </div>`;
      },
    },
    series: chartData.map((d) => d.value),
  };
}
